import type {
  GoogleAdsAdGroup,
  GoogleAdsSearchCreative,
  IGoogleAdsAdGroupKeyword,
  IGoogleAdsAdGroupSitelink,
} from '@feathr/blackbox';

import type { IAd, IAdGroup, IAdGroupKeyword, ISitelink } from './AdStep.types';

/*
 * The only module that knows where the API and the UI disagree: keywords and negative
 * keywords are one array on the wire split by `negative`, a sitelink is
 * `link_text`/`final_url` rather than `text`/`url`, and an ad is a creative whose
 * `destination_url` is the UI's `finalUrl`.
 *
 * Every row's `key` survives the round trip — the API carries a row's Google resource
 * names forward only for keys it recognises, so losing one discards its history.
 */

function toKeywordView(keyword: IGoogleAdsAdGroupKeyword): IAdGroupKeyword {
  return { key: keyword.key, text: keyword.text, matchType: keyword.match_type };
}

function toSitelinkView(sitelink: IGoogleAdsAdGroupSitelink): ISitelink {
  return {
    key: sitelink.key,
    text: sitelink.link_text,
    url: sitelink.final_url,
    description1: sitelink.description1,
    description2: sitelink.description2,
  };
}

/** One ad, read off the creative that backs it. */
export function toAdView(creative: GoogleAdsSearchCreative): IAd {
  return {
    key: creative.id,
    headlines: creative.get('headlines', []),
    descriptions: creative.get('descriptions', []),
    finalUrl: creative.get('destination_url', ''),
  };
}

/** The view an ad group's editor and summary render. `ads` is already filtered to it. */
export function toAdGroupView(
  adGroup: GoogleAdsAdGroup,
  ads: readonly GoogleAdsSearchCreative[],
): IAdGroup {
  const keywords = adGroup.get('keywords', []);

  return {
    key: adGroup.id,
    name: adGroup.get('name', ''),
    keywords: keywords.filter((keyword) => !keyword.negative).map(toKeywordView),
    negativeKeywords: keywords.filter((keyword) => keyword.negative).map(toKeywordView),
    sitelinks: adGroup.get('sitelinks', []).map(toSitelinkView),
    ads: ads.map(toAdView),
  };
}

/** Folds both keyword editors back into the single list the API stores. */
export function toKeywordsPayload(
  keywords: readonly IAdGroupKeyword[],
  negativeKeywords: readonly IAdGroupKeyword[],
): IGoogleAdsAdGroupKeyword[] {
  return [
    ...keywords.map((keyword) => ({
      key: keyword.key,
      text: keyword.text,
      match_type: keyword.matchType,
      negative: false,
    })),
    ...negativeKeywords.map((keyword) => ({
      key: keyword.key,
      text: keyword.text,
      match_type: keyword.matchType,
      negative: true,
    })),
  ];
}

export function toSitelinksPayload(sitelinks: readonly ISitelink[]): IGoogleAdsAdGroupSitelink[] {
  return sitelinks.map((sitelink) => ({
    key: sitelink.key,
    link_text: sitelink.text,
    final_url: sitelink.url,
    description1: sitelink.description1 ?? null,
    description2: sitelink.description2 ?? null,
  }));
}
